import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Reusable SEO component (react-helmet).
 *
 * Props:
 *  - title          : full <title> string (REQUIRED)
 *  - description    : meta description (REQUIRED)
 *  - path           : URL path (e.g. "/abonnements"). Used for canonical + og:url.
 *  - image          : absolute URL or absolute path (defaults to home hero image).
 *  - jsonLd         : optional object or array of Schema.org JSON-LD documents.
 */
const DEFAULT_SITE = 'https://car-main.ch';
const DEFAULT_IMAGE = `${DEFAULT_SITE}/generated/hero-swiss-road.jpg`;

const buildUrl = (path = '/') => {
  if (!path.startsWith('/')) path = '/' + path;
  return `${DEFAULT_SITE}${path}`;
};

const SEO = ({ title, description, path = '/', image, jsonLd }) => {
  const url = buildUrl(path);
  const finalImage = image
    ? (image.startsWith('http') ? image : `${DEFAULT_SITE}${image}`)
    : DEFAULT_IMAGE;

  const jsonLdArray = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="fr_CH" />
      <meta property="og:site_name" content="CarMaintenance" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />

      {/* JSON-LD per-page */}
      {jsonLdArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
